/**
 * Emails — log global de e-mails do jogo (_39_email_log), paginado + filtros
 * (direção, busca livre por nome/título/conteúdo/item/alz). Botão "Enviar Email"
 * abre o modal de mail em massa (broadcast) — mesma ação que ficava na antiga
 * Ferramentas GM, agora padronizada como modal.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mail, RefreshCw, Search, X, AlertCircle, Send, ChevronLeft, ChevronRight,
  Loader2, Gift, Coins, ArrowDownLeft, ArrowUpRight, Clock, Users,
} from 'lucide-react';
import {
  listEmails, mailBroadcast, mailBroadcastPreview,
  type EmailRow, type MailWindowMode, type MailAudienceInput, type MailPreviewResult,
} from '@/shared/services/adminService';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useToast } from '@/shared/contexts/ToastContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import { DataTable } from '@/shared/components/ui/DataTable';
import { DatePicker, todayISO } from '@/shared/components/ui/DatePicker';

const LIMIT = 50;
// itemKindIdx do email pode vir com flags (bits 12-24); limpa pra exibir o id base.
const cleanItemId = (k: number) => k - (k & 0x1fff000);

export default function EmailsPage() {
  const { hasFeature } = useAuth();
  const { notify } = useToast();
  const canSend = hasFeature('mail_broadcast');

  const [rows, setRows] = useState<EmailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState<'all' | 'R' | 'S'>('all');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');       // termo efetivamente buscado
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mailOpen, setMailOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listEmails({ skip: page * LIMIT, limit: LIMIT, direction, search: applied });
      setRows(res.items);
      setTotal(res.total);
      setError(null);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro ao carregar emails');
    } finally {
      setLoading(false);
    }
  }, [page, direction, applied]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doSearch = () => { setPage(0); setApplied(search.trim()); };
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Mail className="text-[#00e1d7]" size={26} /> Emails
          </h1>
          <p className="text-sm text-white/65 mt-1">
            {total.toLocaleString('pt-BR')} registros no log de e-mails do jogo
          </p>
        </div>
        <div className="flex gap-2">
          {canSend && (
            <button
              onClick={() => setMailOpen(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                         bg-purple-500/10 border border-purple-500/30 text-purple-400
                         hover:bg-purple-500/20 transition cursor-pointer"
            >
              <Send size={14} /> Enviar Email
            </button>
          )}
          <button
            onClick={fetchData} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7]
                       hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="surface-card rounded-xl p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="flex gap-1.5">
          {([['all', 'Todos'], ['R', 'Recebidos'], ['S', 'Enviados']] as const).map(([val, lbl]) => (
            <button
              key={val} onClick={() => { setDirection(val); setPage(0); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition border
                ${direction === val
                  ? 'bg-[#00e1d7]/15 text-[#00e1d7] border-[#00e1d7]/40'
                  : 'bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}
            >
              {lbl}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3">
          <Search size={15} className="text-white/40" />
          <input
            type="text" value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }}
            placeholder="Buscar nome, título, conteúdo, item ID ou alz..."
            className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-white/35 outline-none"
          />
          {search && (
            <button onClick={() => { setSearch(''); setApplied(''); setPage(0); }} className="text-white/45 hover:text-white">
              <X size={14} />
            </button>
          )}
        </div>
        <button
          onClick={doSearch}
          className="px-4 py-2 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/30 text-[#00e1d7] text-sm font-semibold hover:bg-[#00e1d7]/25 transition cursor-pointer"
        >
          Buscar
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <DataTable<EmailRow>
        columns={[
          { key: 'date', label: 'Data', width: '150px',
            cell: (r) => (
              <span className="text-[11px] font-mono text-white/60">
                {r.logDatetime ? new Date(r.logDatetime).toLocaleString('pt-BR') : '—'}
              </span>
            ) },
          { key: 'dir', label: 'Dir', width: '90px', align: 'center',
            cell: (r) => r.direction === 'S' ? (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/15 text-blue-400 border border-blue-500/30">
                <ArrowUpRight size={11} /> Enviado
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                <ArrowDownLeft size={11} /> Recebido
              </span>
            ) },
          { key: 'from', label: 'De', width: '1.1fr',
            cell: (r) => (
              <Link to={`/admin/character/${r.senderCharIdx}`} onClick={(e) => e.stopPropagation()}
                    className="min-w-0 hover:text-[#00e1d7] transition inline-block">
                <span className="text-xs text-white truncate">{r.senderName || `Char ${r.senderCharIdx}`}</span>
                <span className="block text-[10px] text-white/40 font-mono">#{r.senderCharIdx}</span>
              </Link>
            ) },
          { key: 'to', label: 'Para', width: '1.1fr',
            cell: (r) => r.receiverCharIdx ? (
              <Link to={`/admin/character/${r.receiverCharIdx}`} onClick={(e) => e.stopPropagation()}
                    className="min-w-0 hover:text-[#00e1d7] transition inline-block">
                <span className="text-xs text-white truncate">{r.receiverName || `Char ${r.receiverCharIdx}`}</span>
                <span className="block text-[10px] text-white/40 font-mono">#{r.receiverCharIdx}</span>
              </Link>
            ) : <span className="text-[11px] text-white/35">—</span> },
          { key: 'title', label: 'Assunto', width: '1.4fr',
            cell: (r) => (
              <div className="min-w-0">
                <p className="text-xs text-white/85 truncate font-medium">{r.title || '—'}</p>
                {r.content && <p className="text-[10px] text-white/40 truncate">{r.content}</p>}
              </div>
            ) },
          { key: 'attach', label: 'Anexo', width: '150px',
            cell: (r) => (
              <div className="flex flex-col gap-0.5">
                {r.itemKindIdx > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-white/70">
                    <Gift size={11} className="text-purple-400" />
                    <span className="font-mono">#{cleanItemId(r.itemKindIdx)}</span>
                  </span>
                )}
                {r.alz > 0 && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-white/70">
                    <Coins size={11} className="text-amber-400" />
                    {r.alz.toLocaleString('pt-BR')}
                  </span>
                )}
                {r.itemKindIdx === 0 && r.alz === 0 && <span className="text-[11px] text-white/25">—</span>}
              </div>
            ) },
        ]}
        rows={rows}
        rowKey={(r) => r.logId}
        loading={loading}
        empty="Nenhum e-mail encontrado"
        mobileTitle={(r) => r.title || '(sem assunto)'}
        mobileSubtitle={(r) => `${r.senderName || '—'} → ${r.receiverName || '—'}`}
      />

      {/* Paginação */}
      <div className="flex items-center justify-between mt-4 text-xs text-white/55">
        <span>Página {page + 1} de {totalPages}</span>
        <div className="flex gap-2">
          <button
            onClick={() => page > 0 && setPage((p) => p - 1)}
            disabled={page === 0 || loading}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/75 hover:bg-white/[0.06] transition cursor-pointer disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <button
            onClick={() => page + 1 < totalPages && setPage((p) => p + 1)}
            disabled={page + 1 >= totalPages || loading}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/75 hover:bg-white/[0.06] transition cursor-pointer disabled:opacity-40"
          >
            Próxima <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {mailOpen && (
        <SendMailModal
          onClose={() => setMailOpen(false)}
          onSent={() => { setMailOpen(false); fetchData(); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────── Modal: enviar mail em massa ──── */

function SendMailModal({ onClose, onSent }: { onClose: () => void; onSent: () => void }) {
  const { notify } = useToast();
  const confirm = useConfirm();
  const [step, setStep] = useState<1 | 2>(1);

  // Fase 1 — público
  const [windowMode, setWindowMode] = useState<MailWindowMode>('hours');
  const [sinceDate, setSinceDate] = useState('');
  const [amount, setAmount] = useState('2');
  const [groupByAccount, setGroupByAccount] = useState(false);
  const [preview, setPreview] = useState<MailPreviewResult | null>(null);
  const [searching, setSearching] = useState(false);

  // Fase 2 — recompensa
  const [reward, setReward] = useState({
    title: '', content: '', itemId: '', itemOption: '0', itemDuration: '0', qty: '1', alz: '0',
  });
  const [busy, setBusy] = useState(false);
  const setR = (k: keyof typeof reward) => (v: string) => setReward((f) => ({ ...f, [k]: v }));
  const inCls = 'w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white';

  // monta o input de audiência; null se inválido (com toast)
  const audience = (): MailAudienceInput | null => {
    if (windowMode === 'date') {
      if (!sinceDate) { notify('error', 'Selecione a data'); return null; }
      return { windowMode, sinceDate, groupByAccount };
    }
    const v = parseFloat(amount.replace(',', '.'));
    if (!isFinite(v) || v <= 0) { notify('error', 'Informe um valor maior que zero'); return null; }
    return { windowMode, value: v, groupByAccount };
  };
  // qualquer mudança na audiência invalida o preview (força nova busca)
  const changeAudience = (fn: () => void) => { fn(); setPreview(null); };

  const doSearch = async () => {
    const aud = audience();
    if (!aud) return;
    setSearching(true);
    try {
      setPreview(await mailBroadcastPreview(aud));
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro na busca');
    } finally {
      setSearching(false);
    }
  };

  const fmtUptime = (sec: number) => {
    const h = Math.floor(sec / 3600);
    return h >= 1 ? `${h}h` : `${Math.floor(sec / 60)}min`;
  };

  const send = async () => {
    const aud = audience();
    if (!aud) return;
    const title = reward.title.trim();
    const content = reward.content.trim();
    if (!title || !content) { notify('error', 'Título e conteúdo são obrigatórios'); return; }
    const ok = await confirm({
      title: 'Enviar email em massa',
      description: `Enviar "${title}" para ${preview?.total ?? '?'} jogador(es) (${preview?.desc ?? ''})? Não pode ser desfeito.`,
      tone: 'warning',
      confirmLabel: 'Enviar',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await mailBroadcast({
        ...aud, title, content,
        itemId: parseInt(reward.itemId, 10) || 0,
        itemOption: parseInt(reward.itemOption, 10) || 0,
        itemDuration: parseInt(reward.itemDuration, 10) || 0,
        qty: Math.max(1, Math.min(parseInt(reward.qty, 10) || 1, 30)),
        alz: parseInt(reward.alz, 10) || 0,
      });
      notify('success', res.message || 'Email enviado');
      onSent();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally {
      setBusy(false);
    }
  };

  const unitLbl = windowMode === 'days' ? 'dias' : windowMode === 'hours' ? 'horas' : 'minutos';

  return (
    <Modal onClose={onClose} maxW="620px">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Send size={18} className="text-purple-400" /> Enviar email em massa
        </h3>
        <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
      </div>

      {/* stepper */}
      <div className="flex items-center gap-2 mb-4 text-[11px] font-bold">
        <span className={step === 1 ? 'text-[#00e1d7]' : 'text-white/35'}>1 · Público</span>
        <div className="flex-1 h-px bg-white/10" />
        <span className={step === 2 ? 'text-[#00e1d7]' : 'text-white/35'}>2 · Recompensa</span>
      </div>

      {step === 1 ? (
        <div className="space-y-4">
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Janela (por último login)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {([['date', 'Desde data'], ['days', 'Dias'], ['hours', 'Horas'], ['minutes', 'Minutos']] as const).map(([val, lbl]) => (
                <button
                  key={val} onClick={() => changeAudience(() => setWindowMode(val))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition cursor-pointer
                    ${windowMode === val
                      ? 'bg-[#00e1d7]/15 border-[#00e1d7]/40 text-[#00e1d7]'
                      : 'bg-white/[0.03] border-white/[0.08] text-white/60 hover:text-white'}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
            {windowMode === 'date' ? (
              <div className="max-w-[240px]">
                <DatePicker value={sinceDate} onChange={(v) => changeAudience(() => setSinceDate(v))} max={todayISO()} />
                <p className="text-[10px] text-white/35 mt-1">Quem logou <b className="text-white/60">desde</b> essa data.</p>
              </div>
            ) : (
              <div className="max-w-[240px]">
                <div className="flex items-center gap-2 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3">
                  <Clock size={15} className="text-white/40 shrink-0" />
                  <input
                    type="text" inputMode="decimal" value={amount}
                    onChange={(e) => changeAudience(() => setAmount(e.target.value.replace(/[^\d.,]/g, '')))}
                    placeholder="2" className="flex-1 bg-transparent py-2 text-sm text-white outline-none" />
                  <span className="text-xs text-white/40 shrink-0">{unitLbl}</span>
                </div>
                <p className="text-[10px] text-white/35 mt-1">Últimas <b className="text-white/60">{amount || '?'}</b> {unitLbl} — aceita decimal (ex: 0.5).</p>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input type="checkbox" checked={groupByAccount}
              onChange={(e) => changeAudience(() => setGroupByAccount(e.target.checked))}
              className="w-4 h-4 accent-[#00e1d7] cursor-pointer" />
            <span className="text-sm text-white/85">Agrupar emails por conta</span>
            <span className="text-[10px] text-white/40">— 1 email/conta, pro char de maior uptime</span>
          </label>

          <button onClick={doSearch} disabled={searching}
            className="w-full px-4 py-2.5 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/30 text-[#00e1d7] font-bold text-sm hover:bg-[#00e1d7]/25 transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Buscar beneficiários
          </button>

          {preview && (
            <div className="rounded-lg border border-white/[0.06] overflow-hidden">
              <div className="px-3 py-2 bg-white/[0.03] flex items-center gap-2 text-sm">
                <Users size={14} className="text-[#00e1d7]" />
                <b className="text-white">{preview.total.toLocaleString('pt-BR')}</b>
                <span className="text-white/60 truncate">beneficiário(s) — {preview.desc}</span>
              </div>
              <div className="max-h-[220px] overflow-y-auto divide-y divide-white/[0.04]">
                {preview.items.map((r) => (
                  <div key={r.charIdx} className="px-3 py-1.5 flex items-center gap-2 text-xs">
                    <span className="text-white/85 flex-1 truncate">{r.name || `Char ${r.charIdx}`}</span>
                    {r.online && <span className="text-emerald-400 text-[10px] font-bold">online</span>}
                    <span className="text-white/35 font-mono">#{r.charIdx}</span>
                    <span className="text-white/30">conta {r.userNum}</span>
                    <span className="text-white/45 w-12 text-right">{fmtUptime(r.playTime)}</span>
                  </div>
                ))}
                {preview.total === 0 && <div className="px-3 py-3 text-xs text-white/35 text-center">Nenhum jogador nessa janela.</div>}
                {preview.total > preview.shown && (
                  <div className="px-3 py-1.5 text-[10px] text-white/35 text-center">
                    …e mais {(preview.total - preview.shown).toLocaleString('pt-BR')} (amostra dos {preview.shown} de maior uptime)
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
              Cancelar
            </button>
            <button onClick={() => setStep(2)} disabled={!preview || preview.total === 0}
              className="flex-1 px-4 py-2.5 rounded-lg bg-purple-500 hover:bg-purple-400 text-white font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
              Continuar <ChevronRight size={16} />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-[#00e1d7]/20 bg-[#00e1d7]/[0.06] px-3 py-2 flex items-center gap-2 text-sm">
            <Users size={14} className="text-[#00e1d7] shrink-0" />
            <span className="text-white/80 truncate">Enviando para <b className="text-white">{preview?.total.toLocaleString('pt-BR')}</b> — {preview?.desc}</span>
          </div>
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Título (máx. 40)</label>
            <input type="text" maxLength={40} value={reward.title}
              onChange={(e) => setR('title')(e.target.value)}
              placeholder="ex: Manutenção concluída" className={inCls} />
          </div>
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Conteúdo (máx. 500)</label>
            <textarea maxLength={500} rows={4} value={reward.content}
              onChange={(e) => setR('content')(e.target.value)}
              placeholder="Mensagem para os jogadores..." className={`${inCls} resize-none`} />
          </div>

          <div className="rounded-lg border border-white/[0.06] p-4">
            <p className="text-[11px] text-white/55 mb-3 flex items-center gap-1.5"><Gift size={13} /> Anexo (opcional)</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider">Item ID</label>
                <input type="text" inputMode="numeric" value={reward.itemId}
                  onChange={(e) => setR('itemId')(e.target.value.replace(/\D/g, ''))} placeholder="0" className={`${inCls} mt-0.5`} />
              </div>
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider">Opção</label>
                <input type="text" inputMode="numeric" value={reward.itemOption}
                  onChange={(e) => setR('itemOption')(e.target.value.replace(/\D/g, ''))} className={`${inCls} mt-0.5`} />
              </div>
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider">Duração</label>
                <input type="text" inputMode="numeric" value={reward.itemDuration}
                  onChange={(e) => setR('itemDuration')(e.target.value.replace(/\D/g, ''))} className={`${inCls} mt-0.5`} />
              </div>
              <div>
                <label className="text-[10px] text-white/40 uppercase tracking-wider">Qtd (1-30)</label>
                <input type="text" inputMode="numeric" value={reward.qty}
                  onChange={(e) => setR('qty')(e.target.value.replace(/\D/g, ''))} className={`${inCls} mt-0.5`} />
              </div>
            </div>
            <div className="mt-3 max-w-[180px]">
              <label className="text-[10px] text-white/40 uppercase tracking-wider flex items-center gap-1"><Coins size={11} /> Alz</label>
              <input type="text" inputMode="numeric" value={reward.alz}
                onChange={(e) => setR('alz')(e.target.value.replace(/\D/g, ''))} className={`${inCls} mt-0.5`} />
            </div>
            <p className="text-[10px] text-white/35 mt-2">O anexo (item/alz) vai só na 1ª mensagem de cada jogador.</p>
          </div>

          <div className="flex gap-2">
            <button onClick={() => setStep(1)}
              className="px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm flex items-center gap-1">
              <ChevronLeft size={16} /> Voltar
            </button>
            <button onClick={send} disabled={busy || !reward.title.trim() || !reward.content.trim()}
              className="flex-1 px-4 py-2.5 rounded-lg bg-purple-500 hover:bg-purple-400 text-white font-bold text-sm transition cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function Modal({ children, onClose, maxW = '600px' }:
  { children: React.ReactNode; onClose: () => void; maxW?: string }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div onClick={(e) => e.stopPropagation()}
           className="surface-card rounded-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
           style={{ maxWidth: maxW }}>
        {children}
      </div>
    </div>
  );
}
